/* CGB Glass theme — vanilla JS, no dependencies. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};

  /* ---------- helpers ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? dec + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  }

  function toast(message) {
    const el = document.getElementById('Toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { el.hidden = true; }, 3500);
  }

  const fmtIn = (n) => `${Math.round(n * 100) / 100}"`;

  /* ---------- header & mobile menu ---------- */
  document.addEventListener('click', (e) => {
    const burger = e.target.closest('[data-menu-toggle]');
    if (burger) {
      const header = burger.closest('.site-header');
      const menu = header && $('[data-mobile-menu]', header);
      if (!menu) return;
      const open = menu.hidden;
      menu.hidden = !open;
      burger.setAttribute('aria-expanded', String(open));
      header.toggleAttribute('data-menu-open', open);
    }
  });

  /* ---------- month name tokens (announcement) ---------- */
  function fillMonthTokens(root = document) {
    const month = new Date().toLocaleString('en-US', { month: 'long' });
    $$('[data-month]', root).forEach((el) => { el.textContent = month; });
  }

  /* ---------- countdowns (real campaigns only) ---------- */
  function startCountdowns(root = document) {
    const els = $$('[data-campaign][data-end]', root);
    if (!els.length) return;
    const tick = () => {
      const now = Date.now() / 1000;
      els.forEach((el) => {
        const left = Number(el.dataset.end) - now;
        if (left <= 0) { el.hidden = true; return; }
        const d = Math.floor(left / 86400);
        const h = Math.floor((left % 86400) / 3600);
        const m = Math.floor((left % 3600) / 60);
        const s = Math.floor(left % 60);
        const pad = (n) => String(n).padStart(2, '0');
        const text = d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
        $$('[data-countdown]', el).forEach((c) => { c.textContent = text; });
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ---------- delivery estimate (business days) ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 4;
      const max = Number(this.dataset.max) || 11;
      const add = (days) => {
        const d = new Date();
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          const wd = d.getDay();
          if (wd !== 0 && wd !== 6) left -= 1;
        }
        return d;
      };
      const opts = { month: 'short', day: 'numeric' };
      const a = add(min).toLocaleDateString('en-US', opts);
      const b = add(max).toLocaleDateString('en-US', opts);
      this.textContent = `${this.dataset.prefix || 'Order today, arrives'} ${a}–${b}`;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) { e.preventDefault(); this.change(remove.dataset.lineRemove, 0); }
        const step = e.target.closest('[data-line-step]');
        if (step) {
          e.preventDefault();
          const line = step.closest('[data-line]');
          const input = line && $('[data-line-qty]', line);
          if (!input) return;
          const next = Math.max(0, (Number(input.value) || 0) + Number(step.dataset.lineStep));
          this.change(line.dataset.line, next);
        }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (!input) return;
        const line = input.closest('[data-line]');
        this.change(line.dataset.line, Math.max(0, Number(input.value) || 0));
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }

    get drawer() { return $('.drawer', this); }

    open() {
      const d = this.drawer;
      if (!d) return;
      this._lastFocus = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      const close = $('[data-cart-close]', d);
      if (close) setTimeout(() => close.focus(), 50);
    }

    close() {
      const d = this.drawer;
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    renderSections(sections) {
      if (!sections) return;
      const html = sections['cart-drawer'];
      if (html) {
        const doc = parseHTML(html);
        const fresh = $('.drawer', doc);
        const wasOpen = this.drawer && this.drawer.classList.contains('is-open');
        if (fresh && this.drawer) {
          this.drawer.innerHTML = fresh.innerHTML;
          if (wasOpen) this.drawer.classList.add('is-open');
        }
        const count = $('[data-cart-count-source]', doc);
        if (count) updateCartCount(count.dataset.cartCountSource);
      }
    }

    async change(line, quantity) {
      const row = $(`[data-line="${line}"]`, this);
      if (row) row.classList.add('is-loading');
      try {
        const res = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not update the cart.');
        this.renderSections(data.sections);
        if (document.body.classList.contains('template-cart')) window.location.reload();
      } catch (err) {
        toast(err.message);
        if (row) row.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
  }

  // Header cart buttons open the drawer instead of navigating.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-open]');
    if (!btn) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || document.body.classList.contains('template-cart')) return;
    e.preventDefault();
    drawer.open();
  });

  /* ---------- quick add modal ---------- */
  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      this.content = $('[data-content]', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }

    async open(url) {
      this._lastFocus = document.activeElement;
      this.hidden = false;
      document.body.classList.add('no-scroll');
      this.content.innerHTML = '';
      this.content.classList.add('is-loading');
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}section_id=quick-add`);
        if (!res.ok) throw new Error('Could not load this design.');
        const doc = parseHTML(await res.text());
        const inner = $('.quick-add', doc);
        this.content.classList.remove('is-loading');
        if (!inner) throw new Error('Could not load this design.');
        this.content.appendChild(document.importNode(inner, true));
        const first = $('select, input, button', this.content);
        if (first) first.focus();
      } catch (err) {
        this.content.classList.remove('is-loading');
        this.content.innerHTML = `<p class="form-error">${err.message}</p>`;
      }
    }

    close() {
      this.hidden = true;
      this.content.innerHTML = '';
      document.body.classList.remove('no-scroll');
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }
  }
  customElements.define('quick-add-modal', QuickAddModal);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const modal = document.getElementById('QuickAddModal');
    if (modal) modal.open(btn.dataset.quickAdd);
    else window.location.href = btn.dataset.quickAdd;
  });

  /* ---------- product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.count = $('[data-count]', this);
      this.index = 0;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-prev]')) this.go(this.index - 1);
        if (e.target.closest('[data-next]')) this.go(this.index + 1);
        const t = e.target.closest('[data-thumb]');
        if (t) this.go(Number(t.dataset.thumb));
      });
      const main = $('[data-gallery-main]', this);
      let x0 = null;
      main.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
    }

    go(i) {
      const n = this.slides.length;
      if (n < 2) return;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === this.index);
        const v = $('video', s);
        if (v && k !== this.index) v.pause();
      });
      this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === this.index));
      if (this.count) this.count.textContent = `${this.index + 1} / ${n}`;
    }

    showMedia(id) {
      if (!id) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.go(i);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- product info (buy box) ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.uid = this.dataset.uid;
      this.productUrl = this.dataset.productUrl;
      this.form = document.getElementById(this.dataset.formId);
      this.mode = this.dataset.mode || 'standard';
      this.orientation = 'landscape';
      this.unit = 'in';
      this.isQuick = this.hasAttribute('data-quick');
      this.fit = null;

      this.normalizeInputs();
      this.applyOrientation();
      this.updateAtcLabel();
      this.syncModeUI();

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-opt]')) this.onOptionChange();
      });
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-fit-w], [data-fit-h]')) this.debouncedFit();
        if (e.target.matches('[data-qty]')) this.updateAtcLabel();
      });
      this.debouncedFit = debounce(() => this.computeFit(), 300);

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
        this.form.addEventListener('input', (e) => { if (e.target.matches('[data-qty]')) this.updateAtcLabel(); });
      }

      this.setupSticky();
      this.setupWhatsApp();

      if (!this.isQuick && window.location.hash === '#custom-size' && $('[data-custom-pane]', this)) {
        this.setMode('custom');
        setTimeout(() => { const pane = $('[data-custom-pane]', this); if (pane) pane.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
      }
    }

    /* inputs rendered by the AJAX section use a placeholder uid */
    normalizeInputs() {
      $$('input[type="radio"][data-opt]', this).forEach((input) => {
        input.name = `${this.uid}-opt-${input.dataset.position}`;
      });
    }

    get sizeSelect() { return $('[data-dyn="size"] select[data-opt]', this); }

    selectedOptionIds() {
      const positions = new Set($$('[data-opt]', this).map((el) => Number(el.dataset.position)));
      return Array.from(positions).sort((a, b) => a - b).map((p) => {
        const sel = $(`select[data-opt][data-position="${p}"]`, this);
        if (sel) return sel.value;
        const radio = $(`input[data-opt][data-position="${p}"]:checked`, this);
        return radio ? radio.value : null;
      }).filter(Boolean);
    }

    async onOptionChange() {
      const ids = this.selectedOptionIds();
      if (!ids.length) return;
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      this.classList.add('is-updating');
      const atc = $('[data-atc]', this);
      if (atc) atc.classList.add('is-loading');
      try {
        const res = await fetch(`${this.productUrl}?option_values=${ids.join(',')}&section_id=product-buybox`, { signal: this.abort.signal });
        const doc = parseHTML(await res.text());
        const response = $('[data-buybox-response]', doc);
        if (!response) throw new Error('Could not update the price.');
        $$('[data-dyn]', this).forEach((target) => {
          const src = $(`[data-dyn="${target.dataset.dyn}"]`, response);
          if (src) target.innerHTML = src.innerHTML;
        });
        this.normalizeInputs();
        this.applyOrientation();
        this.updateAtcLabel();
        if (this.mode === 'custom') this.computeFit(); // prices changed (e.g. thickness): re-pick the cheapest panel
        if (!this.isQuick) {
          const variantId = response.dataset.variantId;
          if (variantId) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variantId);
            url.hash = this.mode === 'custom' ? 'custom-size' : '';
            window.history.replaceState({}, '', url.toString());
          }
          const idInput = $('[data-variant-id]', this);
          const gallery = $('product-gallery', this);
          if (gallery && idInput && idInput.dataset.mediaId) gallery.showMedia(idInput.dataset.mediaId);
        }
      } catch (err) {
        if (err.name !== 'AbortError') toast(err.message);
      } finally {
        this.classList.remove('is-updating');
      }
    }

    onClick(e) {
      const modeBtn = e.target.closest('[data-mode-btn]');
      if (modeBtn) { e.preventDefault(); this.setMode(modeBtn.dataset.modeBtn); return; }
      if (e.target.closest('[data-rotate]')) {
        e.preventDefault();
        this.orientation = this.orientation === 'portrait' ? 'landscape' : 'portrait';
        this.applyOrientation();
        return;
      }
      const unit = e.target.closest('[data-unit]');
      if (unit) { e.preventDefault(); this.setUnit(unit.dataset.unit); return; }
      const step = e.target.closest('[data-qty-step]');
      if (step) {
        e.preventDefault();
        const input = $('[data-qty]', this);
        if (input) {
          input.value = Math.min(10, Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep)));
          this.updateAtcLabel();
        }
        return;
      }
      const pickStd = e.target.closest('[data-pick-standard]');
      if (pickStd) {
        e.preventDefault();
        this.orientation = pickStd.dataset.orientation || 'landscape';
        const sel = this.sizeSelect;
        this.setMode('standard');
        if (sel && sel.value !== pickStd.dataset.pickStandard) {
          sel.value = pickStd.dataset.pickStandard;
          this.onOptionChange();
        } else {
          this.applyOrientation();
        }
        return;
      }
      if (e.target.closest('[data-sticky-add]')) {
        e.preventDefault();
        if (this.form) this.form.requestSubmit();
      }
    }

    /* ----- orientation (one variant per glass size; the shopper picks landscape or portrait) ----- */
    applyOrientation() {
      const sel = this.sizeSelect;
      const prop = $('[data-prop-orient]', this);
      const card = $('[data-std-card]', this);
      if (!sel) { if (card) card.hidden = true; return; }
      const portrait = this.orientation === 'portrait';
      Array.from(sel.options).forEach((o) => {
        if (!o.dataset.w) return;
        let w = Number(o.dataset.w);
        let h = Number(o.dataset.h);
        if (portrait && w !== h) [w, h] = [h, w];
        const price = o.dataset.price ? formatMoney(o.dataset.price) : 'unavailable';
        o.textContent = `${w}"×${h}" (W×H) — ${price}${o.disabled && o.dataset.price ? ' · sold out' : ''}`;
      });
      const cur = sel.options[sel.selectedIndex];
      if (!cur || !cur.dataset.w) { if (card) card.hidden = true; if (prop) prop.disabled = true; return; }
      let w = Number(cur.dataset.w);
      let h = Number(cur.dataset.h);
      const square = w === h;
      if (portrait && !square) [w, h] = [h, w];
      const cm = `${Math.round(w * 2.54)}×${Math.round(h * 2.54)} cm`;
      const label = $('[data-size-label]', this);
      if (label) label.textContent = `${w}"×${h}" (W×H) · ${cm}`;
      if (card) {
        card.hidden = false;
        $('[data-pc-size]', card).textContent = `${w}" wide × ${h}" high`;
        $('[data-pc-sub]', card).textContent = `${cm} · ${square ? 'square' : portrait ? 'portrait' : 'landscape'}`;
        $('[data-pc-price]', card).textContent = cur.dataset.price ? formatMoney(cur.dataset.price) : '';
        $('[data-pc-frame]', card).style.setProperty('--r', (w / h).toFixed(4)); // CSS draws it to scale inside the canvas
        const rotate = $('[data-rotate]', card);
        if (rotate) {
          rotate.hidden = square;
          $('[data-rotate-label]', rotate).textContent = portrait ? 'Rotate to landscape' : 'Rotate to portrait';
        }
      }
      if (prop && this.mode !== 'custom') {
        prop.disabled = square;
        prop.value = square ? '' : `${portrait ? 'Portrait' : 'Landscape'} — ${w}" wide × ${h}" tall`;
      }
      this.updateSticky();
    }

    /* ----- standard / custom size ----- */
    setMode(mode) {
      if (!$('[data-custom-pane]', this) && mode === 'custom') return;
      this.mode = mode;
      this.syncModeUI();
      if (mode === 'custom') {
        this.computeFit();
      } else {
        this.setCustomProps(null);
        this.applyOrientation();
        this.updateAtcLabel();
      }
    }

    syncModeUI() {
      const custom = this.mode === 'custom';
      $$('[role="tab"][data-mode-btn]', this).forEach((b) => b.setAttribute('aria-selected', String(b.dataset.modeBtn === this.mode)));
      const std = $('[data-standard-pane]', this);
      const cus = $('[data-custom-pane]', this);
      if (std) std.hidden = custom;
      if (cus) cus.hidden = !custom;
    }

    setUnit(unit) {
      if (unit === this.unit) return;
      const w = $('[data-fit-w]', this);
      const h = $('[data-fit-h]', this);
      const conv = (input) => {
        const n = parseFloat(input.value);
        if (!(n > 0)) return;
        input.value = unit === 'cm' ? Math.round(n * 2.54 * 10) / 10 : Math.round((n / 2.54) * 8) / 8;
      };
      if (w) conv(w);
      if (h) conv(h);
      this.unit = unit;
      $$('[data-unit]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
      $$('[data-unit-label]', this).forEach((el) => { el.textContent = unit; });
      if (w) { w.placeholder = unit === 'cm' ? '76' : '30'; w.step = unit === 'cm' ? '0.5' : '0.25'; }
      if (h) { h.placeholder = unit === 'cm' ? '61' : '24'; h.step = unit === 'cm' ? '0.5' : '0.25'; }
      this.computeFit();
    }

    readWall() {
      const w = parseFloat(($('[data-fit-w]', this) || {}).value);
      const h = parseFloat(($('[data-fit-h]', this) || {}).value);
      if (!(w > 0) || !(h > 0)) return null;
      const k = this.unit === 'cm' ? 1 / 2.54 : 1;
      return { w: w * k, h: h * k, rawW: w, rawH: h };
    }

    sizeCandidates() {
      const sel = this.sizeSelect;
      if (!sel) return [];
      return Array.from(sel.options)
        .filter((o) => o.dataset.w && o.dataset.price && !o.disabled)
        .map((o) => ({ id: o.value, name: o.dataset.name, w: Number(o.dataset.w), h: Number(o.dataset.h), price: Number(o.dataset.price) }));
    }

    /*
      Custom size pricing: the cheapest standard size that covers the wall in either orientation
      (a 20"W × 30"H wall can be cut from the 20"H × 30"W panel turned on its side).
      Ties go to the smaller panel, then to no rotation. Larger than every panel → quote.
    */
    computeFit() {
      const wall = this.readWall();
      const sizes = this.sizeCandidates();
      if (!wall) { this.fit = null; this.renderFit(); return; }
      const EPS = 0.01;
      let best = null;
      sizes.forEach((s) => {
        const options = [{ rot: false, pw: s.w, ph: s.h }];
        if (s.w !== s.h) options.push({ rot: true, pw: s.h, ph: s.w });
        options.forEach((o) => {
          if (o.pw + EPS < wall.w || o.ph + EPS < wall.h) return;
          const c = { size: s, rot: o.rot, area: s.w * s.h };
          if (!best || c.size.price < best.size.price || (c.size.price === best.size.price && (c.area < best.area || (c.area === best.area && !c.rot && best.rot)))) best = c;
        });
      });
      // Closest standard size that fits inside the wall (offered as an alternative when cheaper).
      let inside = null;
      sizes.forEach((s) => {
        const options = [{ rot: false, pw: s.w, ph: s.h }];
        if (s.w !== s.h) options.push({ rot: true, pw: s.h, ph: s.w });
        options.forEach((o) => {
          if (o.pw > wall.w + 0.25 || o.ph > wall.h + 0.25) return;
          const area = o.pw * o.ph;
          if (!inside || area > inside.area) inside = { size: s, rot: o.rot, pw: o.pw, ph: o.ph, area };
        });
      });
      this.fit = { wall, best, inside };
      this.renderFit();
      const sel = this.sizeSelect;
      if (best && sel && sel.value !== best.size.id) {
        sel.value = best.size.id;
        this.onOptionChange();
      }
    }

    renderFit() {
      const pane = $('[data-custom-pane]', this);
      if (!pane) return;
      const tip = $('[data-fit-tip]', pane);
      const card = $('[data-fit-card]', pane);
      const warn = $('[data-fit-warn]', pane);
      let alt = $('[data-fit-alt]', pane);
      const fit = this.fit;
      if (!fit || !fit.best) {
        tip.hidden = Boolean(fit);
        card.hidden = true;
        warn.hidden = !fit;
        if (alt) alt.remove();
        this.setCustomProps(null);
        this.updateAtcLabel();
        return;
      }
      const { wall, best, inside } = fit;
      tip.hidden = true;
      warn.hidden = true;
      card.hidden = false;
      // Merged card: the panel drawn to the shape of the wall + size + price.
      $('[data-fit-wall]', card).style.setProperty('--r', (wall.w / wall.h).toFixed(4));
      const dim = (inches, raw) => (this.unit === 'cm' ? `${Math.round(raw * 10) / 10} cm` : fmtIn(inches));
      const exact = Math.abs((best.rot ? best.size.h : best.size.w) - wall.w) < 0.13 && Math.abs((best.rot ? best.size.w : best.size.h) - wall.h) < 0.13;
      $('[data-fit-size]', card).textContent = `${dim(wall.w, wall.rawW)} wide × ${dim(wall.h, wall.rawH)} high`;
      const sel = this.sizeSelect;
      const cur = sel && sel.options[sel.selectedIndex];
      const price = cur && cur.value === best.size.id && cur.dataset.price ? Number(cur.dataset.price) : best.size.price;
      $('[data-fit-price]', card).textContent = formatMoney(price);
      const result = card;

      if (!alt) {
        alt = document.createElement('p');
        alt.className = 'opt__note';
        alt.setAttribute('data-fit-alt', '');
        result.insertAdjacentElement('afterend', alt);
      }
      const gw = inside ? Math.max(0, (wall.w - inside.pw) / 2) : 0;
      const gh = inside ? Math.max(0, (wall.h - inside.ph) / 2) : 0;
      // Only suggest a standard panel that leaves a small margin (≤ 3" per side), as in the design.
      if (inside && !exact && inside.size.price < price && inside.size.id && gw <= 3 && gh <= 3) {
        alt.hidden = false;
        alt.innerHTML = `Or save with the standard <strong>${inside.pw}"×${inside.ph}"</strong> panel (${formatMoney(inside.size.price)}), leaving about ${fmtIn(gw)} at each side and ${fmtIn(gh)} top and bottom. <button type="button" class="link-btn" data-pick-standard="${inside.size.id}" data-orientation="${inside.rot ? 'portrait' : 'landscape'}">Choose it</button>`;
      } else {
        alt.hidden = true;
      }
      this.setCustomProps(best, wall);
      this.updateAtcLabel();
    }

    setCustomProps(best, wall) {
      const w = $('[data-prop-w]', this);
      const h = $('[data-prop-h]', this);
      const ref = $('[data-prop-ref]', this);
      const orient = $('[data-prop-orient]', this);
      const on = Boolean(best && wall && this.mode === 'custom');
      [w, h, ref].forEach((el) => { if (el) el.disabled = !on; });
      if (!on) {
        if (this.mode === 'custom' && orient) { orient.disabled = true; orient.value = ''; }
        return;
      }
      // Customer sees only the size they typed (width × height, in their unit). The standard size it is
      // billed as and both units go into _ properties: hidden from the customer, shown on the order in admin.
      const cm = this.unit === 'cm';
      const inW = cm ? wall.rawW / 2.54 : wall.rawW;
      const inH = cm ? wall.rawH / 2.54 : wall.rawH;
      const cmW = cm ? wall.rawW : wall.rawW * 2.54;
      const cmH = cm ? wall.rawH : wall.rawH * 2.54;
      const n = (v, d) => String(Math.round(v * 10 ** d) / 10 ** d);
      if (w) w.value = cm ? `${n(cmW, 1)} × ${n(cmH, 1)} cm (W × H)` : `${n(inW, 2)}" × ${n(inH, 2)}" (W × H)`;
      if (h) h.value = `${n(inW, 2)}" × ${n(inH, 2)}" = ${n(cmW, 1)} × ${n(cmH, 1)} cm (W × H)`;
      if (ref) ref.value = `${best.size.name}${best.rot ? ' (rotated)' : ''}`;
      if (orient) { orient.disabled = true; orient.value = ''; } // the typed W × H already says it
    }

    /* ----- add to cart ----- */
    updateAtcLabel() {
      const atc = $('[data-atc]', this);
      const idInput = $('[data-variant-id]', this);
      const err = $('[data-form-error]', this);
      if (!atc || !idInput) return;
      atc.classList.remove('is-loading');
      const available = idInput.dataset.available === 'true';
      const qty = Math.max(1, Number(($('[data-qty]', this) || {}).value) || 1);
      if (!available) { atc.disabled = true; atc.textContent = 'Sold out'; return; }
      if (this.mode === 'custom' && !(this.fit && this.fit.best)) {
        atc.disabled = true;
        atc.textContent = this.fit ? 'Request a quote for this size' : 'Enter your width and height';
        return;
      }
      atc.disabled = false;
      atc.textContent = `Add to cart — ${formatMoney(Number(idInput.dataset.price) * qty)}`;
      this.updateInstallments(Number(idInput.dataset.price));
      if (err) err.hidden = true;
      this.updateSticky();
    }

    /* Shop Pay Installments line for the selected price, from Shopify's own financing plans
       (the native element only carries the first 250 variants). */
    updateInstallments(cents) {
      const out = $('[data-installments]', this);
      const tpl = $('template[data-payment-terms-source]', this);
      const host = tpl ? tpl.content.querySelector('shopify-payment-terms') : $('shopify-payment-terms', this);
      if (!out || !host) return;
      let meta;
      try { meta = JSON.parse(host.getAttribute('shopify-meta') || '{}'); } catch (e) { out.hidden = true; return; }
      const price = cents / 100;
      const plan = (meta.financing_plans || []).find((p) => price >= p.min_price_amount && price <= p.max_price_amount);
      if (!plan) { out.hidden = true; return; }
      const split = (plan.terms || []).filter((t) => t.loan_type === 'split_pay' && Number(t.apr) === 0).sort((a, b) => b.installments_count - a.installments_count)[0];
      out.textContent = split
        ? `Or pay in ${split.installments_count} interest-free installments of ${formatMoney(Math.round(cents / split.installments_count))} with Shop Pay`
        : 'Or pay over time with Shop Pay';
      out.hidden = false;
    }

    updateSticky() {
      const sticky = $('[data-sticky]', this);
      if (!sticky) return;
      const sub = $('[data-dyn="sticky"]', sticky);
      const sel = this.sizeSelect;
      const cur = sel && sel.options[sel.selectedIndex];
      if (sub && cur && cur.dataset.w && this.mode !== 'custom') {
        let w = Number(cur.dataset.w);
        let h = Number(cur.dataset.h);
        if (this.orientation === 'portrait' && w !== h) [w, h] = [h, w];
        sub.textContent = sub.textContent.replace(/^[^·]*/, `${w}"×${h}" `);
      }
    }

    setupSticky() {
      const sticky = $('[data-sticky]', this);
      const row = $('[data-atc-row]', this);
      if (!sticky || !row || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        const btn = $('[data-sticky-add]', sticky);
        if (btn) btn.tabIndex = show ? 0 : -1;
      });
      io.observe(row);
    }

    setupWhatsApp() {
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-whatsapp-link]');
        if (!link || !this.dataset.whatsapp) return;
        const sel = this.sizeSelect;
        const size = $('[data-size-label]', this);
        const parts = [`Hi! I have a question about "${this.dataset.productTitle}"`];
        if (this.mode === 'custom' && this.fit && this.fit.wall) parts.push(`Custom size: ${$('[data-fit-size]', this).textContent}`);
        else if (sel && size) parts.push(`Size: ${size.textContent}`);
        parts.push(`${this.dataset.shopUrl}${this.productUrl}`);
        link.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(parts.join('\n'))}`;
      });
    }

    async onSubmit(e) {
      e.preventDefault();
      const atc = $('[data-atc]', this);
      const err = $('[data-form-error]', this);
      const showError = (msg) => { if (err) { err.textContent = msg; err.hidden = false; } else toast(msg); };
      if (this.mode === 'custom' && !(this.fit && this.fit.best)) {
        showError(this.fit ? 'This size is larger than a single panel. Please ask us for a quote.' : 'Enter your width and height to get the price for your size.');
        const pane = $('[data-custom-pane]', this);
        if (pane) pane.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const upload = $('[data-upload][required]', this);
      if (upload && !upload.files.length) { showError('Please upload your design.'); upload.focus(); return; }
      if (atc) atc.classList.add('is-loading');
      if (err) err.hidden = true;
      const data = new FormData(this.form);
      // drop empty properties (e.g. blank design notes) so the order doesn't list empty fields
      [...data.keys()].filter((k) => k.startsWith('properties[') && data.get(k) === '').forEach((k) => data.delete(k));
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart.');
        const drawer = document.querySelector('cart-drawer');
        const modal = this.closest('quick-add-modal');
        if (modal) modal.close();
        if (drawer) { drawer.renderSections(json.sections); drawer.open(); }
        else window.location.href = routes.cart || '/cart';
      } catch (error) {
        showError(error.message);
      } finally {
        if (atc) atc.classList.remove('is-loading');
        this.updateAtcLabel();
      }
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- home: collection tabs ---------- */
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.panel = $('[data-tab-panel]', this);
      this.footer = $('[data-tab-footer]', this);
      this.cache = {};
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-tab-url]');
        if (!chip) return;
        e.preventDefault();
        this.select(chip);
      });
    }

    async select(chip) {
      $$('[data-tab-url]', this).forEach((c) => c.setAttribute('aria-selected', String(c === chip)));
      const url = chip.dataset.tabUrl;
      const limit = this.dataset.limit || 8;
      this.panel.classList.add('is-loading');
      try {
        let html = this.cache[url];
        if (!html) {
          const sep = url.includes('?') ? '&' : '?';
          const res = await fetch(`${url}${sep}section_id=collection-grid-ajax`);
          html = await res.text();
          this.cache[url] = html;
        }
        const doc = parseHTML(html);
        const grid = $('[data-ajax-grid]', doc);
        const items = grid ? $$('[data-ajax-item]', grid).slice(0, Number(limit)) : [];
        this.panel.innerHTML = '';
        const list = document.createElement('ul');
        list.className = 'product-grid';
        items.forEach((li) => list.appendChild(document.importNode(li, true)));
        this.panel.appendChild(list);
        if (this.footer) {
          const total = Number(chip.dataset.count) || items.length;
          const name = chip.dataset.name || '';
          const showing = $('[data-tab-showing]', this.footer);
          const link = $('[data-tab-link]', this.footer);
          if (showing) showing.textContent = `Showing ${items.length} of ${total} ${name} designs`.replace(/\s+/g, ' ');
          if (link) { link.href = chip.dataset.link || url; link.textContent = `View all ${total} ${name} designs`.replace(/\s+/g, ' '); }
        }
      } catch (err) {
        toast('Could not load these designs.');
      } finally {
        this.panel.classList.remove('is-loading');
      }
    }
  }
  customElements.define('collection-tabs', CollectionTabs);

  /* ---------- product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) { io.disconnect(); this.load(); }
      }, { rootMargin: '400px' });
      io.observe(this);
    }

    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = $('product-recommendations', doc);
        if (fresh && $('[data-ajax-item]', fresh)) this.innerHTML = fresh.innerHTML;
        else this.closest('.shopify-section')?.remove();
      } catch (e) { /* keep empty */ }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- design upload: show the chosen file name (English, unlike the native control) ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-upload]');
    if (!input) return;
    const pick = input.closest('.filepick');
    const name = pick && $('[data-upload-name]', pick);
    if (!name) return;
    const file = input.files && input.files[0];
    name.textContent = file ? file.name : 'No file selected';
    pick.classList.toggle('has-file', !!file);
  });

  /* ---------- collection page: sort & filters auto-submit ---------- */
  document.addEventListener('change', (e) => {
    const el = e.target.closest('[data-autosubmit]');
    if (el && el.form) el.form.submit();
  });

  /* ---------- cart page: qty auto-update ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-page-qty]');
    if (input && input.form) input.form.submit();
  });

  /* ---------- init ---------- */
  const init = () => {
    fillMonthTokens();
    startCountdowns();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
